import React, { useState } from "react";
import { useParams } from "react-router-dom";
import Breadcrumbs from "../../../Breadcrumbs/Breadcrumbs.js";
import useSEO from "../../../hooks/useSEO.js";

import data from "../../../Data/staffData/staffData.json";
import publicationsData from "../../../Data/publicationsData1.json";

// =============================================
// SEO HELPERS
// =============================================
const CITY = "Екатеринбург";

function detectSpecialization(person) {
  const text =
    `${person?.position || ""} ${person?.bio?.join(" ") || ""}`.toLowerCase();

  if (text.includes("дермат")) return "преподаватель дерматовенерологии";
  if (text.includes("гинек")) return "преподаватель гинекологии";
  if (text.includes("уролог")) return "преподаватель урологии";
  if (text.includes("биолог")) return "преподаватель биологии";
  if (text.includes("педагог")) return "преподаватель педагогических дисциплин";
  if (text.includes("профессор")) return "профессор";
  if (text.includes("доцент")) return "доцент";

  return "преподаватель";
}

function cleanText(text = "") {
  return text.replace(/\s+/g, " ").replace(/"/g, "").trim();
}

function extractBioFacts(person) {
  if (!person?.bio || !Array.isArray(person.bio)) return [];

  return person.bio
    .map((item) => cleanText(item))
    .filter(Boolean)
    .slice(0, 5);
}

function buildDescription(person, spec) {
  if (!person) {
    return "Страница преподавателя УрНИИДВиИ.";
  }

  const parts = [
    `${person.name} — ${spec} УрНИИДВиИ.`,
    cleanText(person.position),
    "Профессорско-преподавательский состав образовательного отдела.",
  ];

  return parts.filter(Boolean).join(" ").replace(/\s+/g, " ").slice(0, 300);
}

function buildKeywords(person, spec) {
  if (!person) {
    return [
      "преподаватели УрНИИДВиИ",
      "профессорско-преподавательский состав",
      "медицинское образование",
    ].join(", ");
  }

  return [
    person.name,
    `${person.name} УРНИИДВиИ`,
    spec,
    `${spec} ${CITY}`,
    "преподаватели УрНИИДВиИ",
    "профессорско-преподавательский состав",
    "ординатура дерматовенерология",
    "дополнительное профессиональное образование",
    "медицинское образование",
    "УрНИИДВиИ",
  ]
    .filter(Boolean)
    .join(", ");
}

function buildSeoText(person, spec) {
  if (!person) {
    return "Информация о преподавателе образовательных программ УРНИИДВиИ.";
  }

  const facts = extractBioFacts(person);

  const intro = `${person.name} — ${spec} УРНИИДВиИ в ${CITY}.`;
  const position = cleanText(person.position);
  const factsText = facts.join(" ");
  const finalText =
    "Преподаватель участвует в реализации программ ординатуры и дополнительного профессионального образования. Запись на обучение и подробная информация доступны на сайте института.";

  return [intro, position, factsText, finalText]
    .filter(Boolean)
    .join("\n\n")
    .trim();
}

// =============================================
// COMPONENT
// =============================================

export default function StaffDetail() {
  const { slug } = useParams();

  const [showPublications, setShowPublications] = useState(false);

  const person = data.find((p) => p.slug?.toString() === slug);

  const personPublications = publicationsData.filter(
    (publication) =>
      publication.authors && publication.authors.includes(person?.slug),
  );
  const spec = detectSpecialization(person);
  const isPersonFound = Boolean(person);

  // Хук должен вызываться всегда
  useSEO({
    title: isPersonFound
      ? `${person.name} — ${spec} | УРНИИДВиИ Екатеринбург`
      : "Преподаватель не найден | УРНИИДВиИ",

    description: buildDescription(person, spec),

    keywords: buildKeywords(person, spec),

    url: isPersonFound
      ? `/education/teaching-staff/${person.slug}`
      : "/education/teaching-staff",

    type: "profile",

    noindex: !isPersonFound,
  });

  if (!person) {
    return (
      <section className="contacts education staff-detail">
        <Breadcrumbs />
        <p>Сотрудник не найден</p>
      </section>
    );
  }

  const seoText = buildSeoText(person, spec);

  return (
    <section className="contacts education staff-detail">
      <Breadcrumbs />

      <div className="staff-detail__wrapper">
        <div className="staff-detail__photo">
          <img src={person.photo} alt={person.name} />
        </div>

        <div className="staff-detail__info">
          <h1 className="staff-detail__name">{person.name}</h1>

          <h4 className="staff-detail__position">{person.position}</h4>

          {person.bio && (
            <div className="staff-detail__bio">
              {person.bio.filter(Boolean).map((block, index) => (
                <p key={index}>{block}</p>
              ))}
            </div>
          )}

          {personPublications.length > 0 && (
            <div className="allnews__blank-link">
              <h4 className="staff-detail__position">Научные публикации</h4>
              <button
                onClick={() => setShowPublications(!showPublications)}
                className="prevention__button news__button licenses__toggle-btn"
              >
                {showPublications ? "Скрыть публикации" : "Показать публикации"}
              </button>
            </div>
          )}

          {showPublications && personPublications.length > 0 && (
            <div className="staff-detail__publications">
              <ul className="staff-detail__publications-list">
                {personPublications.map((publication) => (
                  <li
                    key={publication.id}
                    className="staff-detail__publication"
                  >
                    <p>{publication.title}</p>

                    {publication.files?.map((file, index) => (
                      <a
                        key={index}
                        href={file.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="structure__download-link licenses__link"
                      >
                        📄 Открыть PDF
                      </a>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* SEO-текст для индексации */}
          <div
            className="visually-hidden"
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-9999px",
              width: "1px",
              height: "1px",
              overflow: "hidden",
            }}
          >
            <p>{seoText}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
